import { useMemo, useState } from 'react';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import {
  AlertTriangle,
  CalendarX,
  ClipboardList,
  GripVertical,
  Clock,
  Lock,
  MessageSquare,
  Repeat,
  Search,
  Undo2,
  X,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { formatDate, toDateKey, weekdayName } from '@/lib/dates';
import { formatHours, shiftMinutes, shiftsLabel } from '../hours';
import type { HoursRow, WeekAssignment, WeekBoard, WeekStaff } from './weekApi';

const hhmm = (time: string) => time.slice(0, 5);

/**
 * הלוח עצמו: ימים כעמודות, משמרות כשורות.
 *
 * הסניף אינו ציר שלישי אלא קבוצה בתוך התא, כי השאלה שהמסך הזה
 * עונה עליה היא "מי עובד ביום שלישי בבוקר", ולא "מה קורה בסניף
 * אחד". כשבוחרים סניף אחד הקבוצה מתכנסת לעצמה וזה נראה כמו לוח
 * של סניף; כשבוחרים כמה, הצבע הוא מה שמפריד ביניהם.
 */
export function WeekGrid({
  board,
  isDragging,
  onRemove,
  onOpen,
  onClose,
  onReopen,
  taskCounts,
  specialDays,
  highlightedId = null,
  people,
}: {
  board: WeekBoard;
  isDragging: boolean;
  onRemove: (assignmentId: string) => void;
  onOpen: (assignment: WeekAssignment) => void;
  onClose: (cell: { branchId: string; date: string; shiftTypeId: string }) => void;
  onReopen: (cell: { branch_id: string; date: string; shift_type_id: string | null }) => void;
  /** כמה משימות לכל שיבוץ, לפי מזהה שיבוץ. */
  taskCounts: Map<string, number> | undefined;
  /** ימים מיוחדים לפי תאריך, להצגה בכותרת היום. */
  specialDays: Map<string, { name: string; kind: string }[]> | undefined;
  /**
   * העובד שסומן ברשימת הצוות.
   *
   * כשמישהו מסומן, כל השאר מועמים. ההארה לבדה לא הספיקה: בלוח
   * מלא העין לא מוצאת שלושה ריבועים מודגשים מתוך ארבעים.
   */
  highlightedId?: string | null;
  /**
   * כל הצוות של הרשת, לחיפוש שם ותפקיד בלבד.
   *
   * board.staff של טבלת סניף מכיל רק את הצוות שלו, ועובד מסניף
   * אחר ששובץ כאן אינו בו: השם שלו יצא ריק, והספירה לפי תפקיד
   * הפסיקה לראות אותו — משמרת נראתה חסרה כשהיא מלאה.
   */
  people?: WeekStaff[];
}) {
  const today = toDateKey(new Date());

  const closureFor = (branchId: string, date: string, shiftTypeId: string) =>
    board.closures.find(
      (closure) =>
        closure.branch_id === branchId &&
        closure.is_closed &&
        (closure.date === date || closure.date === null) &&
        (closure.shift_type_id === null || closure.shift_type_id === shiftTypeId),
    );

  /**
   * יום שכל המשמרות בו סגורות.
   *
   * עמודה כזו מקבלת רוחב מוקטן: היא מחזיקה את המילה "שבת" ותו
   * לא, ובטבלה של שבעה ימים היא לקחה שביעית מהרוחב מאף אחד.
   */
  const fullyClosed = (day: string) =>
    board.branches.length > 0 &&
    board.branches.every((branch) =>
      board.shift_types.every((shift) => closureFor(branch.id, day, shift.id)),
    );

  return (
    <div className="overflow-x-auto rounded-sm border border-line bg-raised">
      {/*
        רוחב מינימלי שמכניס שבעה ימים בלי גלילה אופקית במסך
        רגיל. גלילה בלוח שבועי פירושה שאי אפשר לראות את השבוע,
        וזו כל מטרת המסך.
      */}
      <table className="w-full min-w-[44rem] table-fixed border-collapse">
        <thead>
          <tr>
            <th className="w-20 border-b border-line bg-cream p-1.5 text-start text-label text-muted">
              משמרת
            </th>
            {board.days.map((day) => (
              <th
                key={day}
                className={cn(
                  'border-b border-line p-2 text-center text-label',
                  day === today ? 'bg-olive-soft text-ink' : 'bg-cream text-muted',
                  // table-fixed לוקח את הרוחב מהשורה הראשונה.
                  // לא צר מהתוכן: תווית הסגירה וכפתור הפתיחה מחדש
                  // אינם ניתנים לכיווץ, וצרות מדי הוציאה אותם מהתא
                  // ואיתם סרגל גלילה על הטבלה כולה.
                  fullyClosed(day) && 'w-20',
                )}
              >
                <span className="grid gap-0.5">
                  <span className="font-medium">{weekdayName(day)}</span>
                  <span className="tabular" dir="ltr">
                    {formatDate(day).slice(0, 5)}
                  </span>
                  {/*
                    גם יום שהסניף פתוח בו הוא מידע שמשנה שיבוץ:
                    מי שיודע שזה ערב חג משבץ אחרת, גם בלי כלל
                    סגירה. לכן זה מופיע תמיד, ולא רק כשסגור.
                  */}
                  {(specialDays?.get(day) ?? []).length > 0 && (
                    <span
                      className="truncate font-normal text-olive"
                      title={(specialDays?.get(day) ?? []).map((one) => one.name).join(' · ')}
                    >
                      {(specialDays?.get(day) ?? []).map((one) => one.name).join(' · ')}
                    </span>
                  )}
                </span>
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {/*
            גוון מתחלף לשורה. שתי שורות משמרת נראו זהות לחלוטין,
            ובסריקה מהירה העין איבדה את ההפרדה ביניהן באמצע השבוע.
            הגוון אינו ההודעה — שם המשמרת והשעות הם — הוא רק מחזיק
            את השורה יחד לאורך שבעה ימים.
          */}
          {board.shift_types.map((shift, index) => (
            <tr key={shift.id} className={cn(index % 2 === 1 && 'bg-cream/50')}>
              <th className="border-b border-line bg-cream p-1.5 text-start align-top">
                <span className="grid gap-0.5">
                  <span className="text-body-sm font-medium text-ink">{shift.name}</span>
                  <span className="tabular text-label text-muted" dir="ltr">
                    {hhmm(shift.start_time)}–{hhmm(shift.end_time)}
                  </span>
                </span>
              </th>

              {board.days.map((day) => (
                <td
                  key={day}
                  className={cn(
                    'border-b border-line p-1 align-top',
                    day === today && 'bg-olive-soft/30',
                  )}
                >
                  <div className="grid min-w-0 gap-1">
                    {board.branches.map((branch) => {
                      const closed = closureFor(branch.id, day, shift.id);
                      if (closed) {
                        // כלל קבוע ("שבת") נערך במסך ימים סגורים, כי
                        // הוא חל על כל השבועות. רק סגירה של תאריך
                        // מסוים נפתחת מכאן.
                        const isThisDate = closed.date === day;
                        return (
                          <p
                            key={branch.id}
                            className="m-0 flex items-center gap-1 rounded-sm border border-dashed border-line px-1.5 py-1 text-label text-muted"
                          >
                            {/* בלי שם הסניף: הטבלה כולה היא של סניף
                                אחד, והשם כבר בכותרת שלה */}
                            <span className="min-w-0 grow truncate">{closed.label}</span>
                            {isThisDate && (
                              <button
                                type="button"
                                aria-label={`פתיחה מחדש של ${branch.name} ב${weekdayName(day)}`}
                                onClick={() =>
                                  onReopen({
                                    branch_id: branch.id,
                                    date: day,
                                    shift_type_id: closed.shift_type_id,
                                  })
                                }
                                className="grid size-5 shrink-0 cursor-pointer place-items-center rounded-sm bg-transparent text-muted hover:bg-cream hover:text-ink"
                              >
                                <Undo2 size={12} strokeWidth={2} aria-hidden="true" />
                              </button>
                            )}
                          </p>
                        );
                      }

                      return (
                        <ShiftCell
                          key={branch.id}
                          board={board}
                          branchId={branch.id}
                          date={day}
                          shiftTypeId={shift.id}
                          isDragging={isDragging}
                          onRemove={onRemove}
                          onOpen={onOpen}
                          onClose={onClose}
                          taskCounts={taskCounts}
                          highlightedId={highlightedId}
                          people={people ?? board.staff}
                        />
                      );
                    })}
                  </div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** תא אחד: סניף, יום ומשמרת. זה מה שמקבל את הגרירה. */
export function ShiftCell({
  board,
  branchId,
  date,
  shiftTypeId,
  isDragging,
  onRemove,
  onOpen,
  onClose,
  taskCounts,
  highlightedId,
  people,
}: {
  board: WeekBoard;
  branchId: string;
  date: string;
  shiftTypeId: string;
  isDragging: boolean;
  onRemove: (assignmentId: string) => void;
  onOpen: (assignment: WeekAssignment) => void;
  onClose: (cell: { branchId: string; date: string; shiftTypeId: string }) => void;
  taskCounts: Map<string, number> | undefined;
  highlightedId: string | null;
  people: WeekStaff[];
}) {
  const { setNodeRef, isOver } = useDroppable({ id: `${branchId}|${date}|${shiftTypeId}` });

  const branch = board.branches.find((candidate) => candidate.id === branchId);
  const isPublished = branch?.status === 'published';

  const here = board.assignments.filter(
    (assignment) =>
      assignment.branch_id === branchId &&
      assignment.date === date &&
      assignment.shift_type_id === shiftTypeId,
  );

  const weekday = new Date(`${date}T12:00:00`).getDay();
  const rules = board.requirements.filter(
    (requirement) =>
      requirement.branch_id === branchId &&
      requirement.shift_type_id === shiftTypeId &&
      requirement.weekday === weekday,
  );

  const required = rules.reduce((total, rule) => total + rule.required_count, 0);
  const missing = Math.max(0, required - here.length);

  // דרישה לתפקיד מסוים נספרת בנפרד: משמרת עם חמישה מוכרים
  // וקופאי אחד חסר אינה משמרת מלאה, גם אם המספר הכולל מסתדר.
  const byTitle = rules
    .filter((rule) => rule.job_title_id !== null)
    .map((rule) => ({
      name: rule.job_title ?? '',
      required: rule.required_count,
      filled: here.filter((assignment) => {
        const person = people.find((one) => one.id === assignment.employee_id);
        return person?.job_title_id === rule.job_title_id;
      }).length,
    }))
    .filter((row) => row.filled < row.required);

  // תא בלי דרישה ובלי שיבוץ אינו מידע. הוא מופיע רק בזמן גרירה,
  // כדי שיהיה לאן להפיל גם במשמרת שלא הוגדרה לה דרישה.
  if (required === 0 && here.length === 0 && !isDragging) return null;

  return (
    <div
      ref={setNodeRef}
      className={cn(
        'group/cell grid gap-1 rounded-sm border p-1 transition-colors',
        isOver && 'border-ink bg-cream',
        !isOver && isDragging && 'border-dashed border-line',
        !isOver && !isDragging && 'border-transparent',
      )}
      style={
        !isOver && branch?.color
          ? { borderInlineStartColor: branch.color, borderInlineStartWidth: 3 }
          : undefined
      }
    >
      {here.map((assignment) => (
        <AssignmentChip
          key={assignment.id}
          board={board}
          assignment={assignment}
          isPublished={isPublished}
          onRemove={() => onRemove(assignment.id)}
          onOpen={() => onOpen(assignment)}
          tasks={taskCounts?.get(assignment.id) ?? 0}
          people={people}
          highlight={
            highlightedId === null
              ? 'none'
              : assignment.employee_id === highlightedId
                ? 'on'
                : 'off'
          }
        />
      ))}

      {/*
        מצב האיוש ומספר המשובצים מול הדרוש, בשורה אחת.

        שם הסניף היה כאן וירד: הוא כבר בכותרת של הטבלה.

        והמילה מופיעה רק כשהיא אומרת משהו. בתא ריק "חסר עובד 0/1"
        הוא אותו משפט שישים פעם על המסך, והמשולש והמספר כבר אומרים
        אותו; בתא חלקי "חסר עובד 1/2" הוא מידע שאי אפשר לקרוא
        מהמספר בלי לחשב. קוראי מסך מקבלים את המשפט תמיד.
      */}
      <p
        className={cn(
          'm-0 flex items-center gap-1 px-1 text-label',
          missing > 0 ? 'text-warn' : 'text-muted',
        )}
      >
        {/* הצבע לבדו אינו ההודעה (כלל 10): המשולש נושא אותה בתא
            ריק, והמילה בתא חלקי */}
        {missing > 0 && <AlertTriangle size={11} strokeWidth={2} aria-hidden="true" />}
        {missing > 0 && here.length > 0 && (
          <span className="min-w-0 truncate">
            {missing === 1 ? 'חסר עובד' : `חסרים ${missing} עובדים`}
          </span>
        )}
        <span className="tabular" dir="ltr">
          {here.length}/{required}
        </span>
        <span className="sr-only">
          {missing === 0
            ? 'מאויש במלואו'
            : missing === 1
              ? 'חסר עובד אחד'
              : `חסרים ${missing} עובדים`}
        </span>

        {!isPublished && (
          <button
            type="button"
            aria-label={`סגירת ${branch?.name ?? ''} ב${weekdayName(date)}`}
            onClick={() => onClose({ branchId, date, shiftTypeId })}
            // מופיע בריחוף ובמיקוד בלבד: שישה כפתורים בכל תא כפול
            // שבעה ימים הם רעש, והפעולה אינה שכיחה
            className="ms-auto grid size-5 shrink-0 cursor-pointer place-items-center rounded-sm bg-transparent text-muted opacity-0 hover:bg-cream hover:text-ink focus-visible:opacity-100 group-hover/cell:opacity-100"
          >
            <CalendarX size={12} strokeWidth={2} aria-hidden="true" />
          </button>
        )}
      </p>

      {byTitle.map((row) => (
        <p key={row.name} className="m-0 flex items-center gap-1 px-1 text-label text-warn">
          <AlertTriangle size={11} strokeWidth={2} aria-hidden="true" />
          <span className="min-w-0 truncate">חסר {row.name}</span>
          <span className="tabular" dir="ltr">
            {row.filled}/{row.required}
          </span>
        </p>
      ))}
    </div>
  );
}

/** שיבוץ אחד: שם, שעות, וצבע הסניף שאליו הוא שייך. */
function AssignmentChip({
  board,
  assignment,
  isPublished,
  onRemove,
  onOpen,
  tasks,
  people,
  highlight = 'none',
}: {
  board: WeekBoard;
  assignment: WeekAssignment;
  isPublished: boolean;
  onRemove: () => void;
  onOpen: () => void;
  /** כמה משימות ניתנו לעובד במשמרת הזו. */
  tasks: number;
  /** כל הצוות, כדי שגם שם של עובד מסניף אחר יימצא. */
  people: WeekStaff[];
  /**
   * 'none' = אף אחד לא מסומן. 'on' = זה שסומן. 'off' = כל השאר.
   *
   * שלושה מצבים ולא דגל: "לא מסומן" ו"מסומן מישהו אחר" צריכים
   * להיראות שונה, אחרת לוח בלי סימון נראה כמו לוח שכולו מועם.
   */
  highlight?: 'none' | 'on' | 'off';
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `assignment:${assignment.id}`,
    data: { kind: 'assignment', assignment },
    disabled: isPublished,
  });

  const person = people.find((candidate) => candidate.id === assignment.employee_id);
  const branch = board.branches.find((candidate) => candidate.id === assignment.branch_id);

  // שובץ מי שסימן שאינו יכול. הסידור לא נחסם, אבל זה נאמר כאן
  // ולא במסך אחר, כי כאן זה נעשה.
  const blocked = board.constraints.some(
    (constraint) =>
      constraint.employee_id === assignment.employee_id &&
      constraint.date === assignment.date &&
      constraint.is_blocking &&
      (constraint.shift_type_id === null || constraint.shift_type_id === assignment.shift_type_id),
  );

  return (
    <div
      className={cn(
        'flex items-center gap-1 rounded-sm px-1.5 py-1 text-label transition-opacity',
        isDragging && 'opacity-40',
        blocked ? 'bg-bad/10 text-bad' : 'bg-cream text-ink',
        highlight === 'on' && 'ring-2 ring-ink',
        highlight === 'off' && 'opacity-30',
      )}
      style={!blocked && branch?.color ? { backgroundColor: `${branch.color}40` } : undefined}
    >
      <button
        type="button"
        ref={setNodeRef}
        {...attributes}
        {...listeners}
        onClick={onOpen}
        aria-label={`הגדרות המשמרת של ${person?.name ?? ''}, ${branch?.name ?? ''}, ${hhmm(assignment.effective_start)} עד ${hhmm(assignment.effective_end)}`}
        // גם גרירה וגם לחיצה: החיישן מופעל רק אחרי תנועה של שישה
        // פיקסלים, ולכן לחיצה במקום נשארת לחיצה
        className={cn(
          'grid min-w-0 grow justify-items-start rounded-sm bg-transparent p-0 text-start underline-offset-2 hover:underline',
          isPublished ? 'cursor-pointer' : 'cursor-grab touch-none',
        )}
      >
        {/*
          השם נשבר לשורה שנייה ואינו נחתך. "אולגה קוטל..." אינו שם,
          ומי שמסתכל על הלוח צריך לדעת את מי הוא רואה בלי לרחף.
          items-start כדי שאייקון האזהרה יישאר בשורה הראשונה.
        */}
        <span className="flex w-full min-w-0 items-start gap-1">
          {blocked && (
            <AlertTriangle size={11} strokeWidth={2} aria-hidden="true" className="mt-0.5 shrink-0" />
          )}
          <span className="min-w-0 font-medium break-words leading-tight">
            {person?.name ?? '—'}
          </span>
        </span>
        <span className="flex w-full items-center gap-1">
          <span className="tabular whitespace-nowrap text-muted" dir="ltr">
            {hhmm(assignment.effective_start)}–{hhmm(assignment.effective_end)}
          </span>
          {assignment.start_time !== null && (
            <Clock
              size={11}
              strokeWidth={2}
              aria-label="שעות מיוחדות"
              className="shrink-0 text-muted"
            />
          )}
          {assignment.note && (
            <MessageSquare
              size={11}
              strokeWidth={2}
              aria-label="יש הערה"
              className="shrink-0 text-muted"
            />
          )}
          {assignment.source === 'recurring' && (
            <Repeat
              size={11}
              strokeWidth={2}
              aria-label="משמרת קבועה"
              className="shrink-0 text-muted"
            />
          )}
          {tasks > 0 && (
            // המספר לצד האייקון ולא האייקון לבד: "יש משימות" ו"יש
            // ארבע משימות" הן שתי תשובות שונות למנהל שמחלק עבודה
            <span
              className="inline-flex shrink-0 items-center gap-0.5 text-ink"
              aria-label={tasks === 1 ? 'משימה אחת' : `${tasks} משימות`}
            >
              <ClipboardList size={11} strokeWidth={2} aria-hidden="true" />
              <span className="tabular">{tasks}</span>
            </span>
          )}
        </span>
      </button>

      {isPublished ? (
        <Lock
          size={12}
          strokeWidth={1.75}
          aria-label="הסידור פורסם"
          className="shrink-0 text-muted"
        />
      ) : (
        <button
          type="button"
          onClick={onRemove}
          aria-label={`הסרת ${person?.name ?? ''} מהמשמרת`}
          className="grid size-5 shrink-0 cursor-pointer place-items-center rounded-sm bg-transparent text-muted hover:bg-raised hover:text-ink"
        >
          <X size={12} strokeWidth={2} aria-hidden="true" />
        </button>
      )}
    </div>
  );
}

/**
 * רשימת העובדים, האילוצים שלהם, ומכאן גוררים אותם ללוח.
 *
 * האילוץ מוצג על העובד ולא במסך נפרד, כי זה הרגע שבו הוא רלוונטי:
 * מי שמשבץ רואה "לא יכול שישי" לפני שהוא גורר, ולא אחרי שהשרת
 * סירב. מספר המשמרות שכבר שובצו נמצא שם מאותה סיבה.
 *
 * רצועה מאוזנת מתחת לכותרת הסניף, ולא עמודה לצד הלוח. בעמודה
 * גובה הסניף נקבע לפי רשימת הצוות ולא לפי הלוח: סניף עם שתי
 * משמרות ביום תפס מסך שלם, וביניהם נפערו רווחים שאי אפשר היה
 * לסגור. כאן הגובה הוא גובה שורת כרטיסים אחת, והלוח מקבל את כל
 * הרוחב — שבעה ימים נכנסים בלי לדחוס.
 */
export function StaffPanel({
  board,
  monthly,
  monthLabel,
  onEditBudget,
  branchName,
  otherStaff = [],
  branchNames,
  highlightedId,
  onHighlight,
  onClearWeek,
  allAssignments,
  panelId,
}: {
  board: WeekBoard;
  /** דקות משובצות החודש, לפי מזהה עובד. ריק בזמן טעינה. */
  monthly: Map<string, HoursRow> | undefined;
  monthLabel: string;
  onEditBudget: (person: WeekStaff, defaults: HoursRow | undefined) => void;
  /** שם הסניף שהפאנל הזה שייך לו, לכותרת. */
  branchName?: string;
  /** עובדים מסניפים אחרים, שנפתחים לפי דרישה. */
  otherStaff?: WeekStaff[];
  /** שם לכל מזהה סניף, כדי לומר מאיפה מגיע מי שנוסף. */
  branchNames?: Map<string, string>;
  /** העובד שסומן, כדי להאיר את השיבוצים שלו בלוח. */
  highlightedId?: string | null;
  onHighlight?: (employeeId: string | null) => void;
  /** הסרה של עובד מכל השבוע בלחיצה אחת. */
  onClearWeek?: (person: WeekStaff, shifts: number) => void;
  /**
   * כל השיבוצים של השבוע, מכל הסניפים.
   *
   * מכסת השעות היא של האדם ולא של הסניף: מי שעובד בשני סניפים
   * ראה כאן רק חצי מהשעות שלו, והמד הראה מקום פנוי שאינו קיים.
   */
  allAssignments?: WeekAssignment[];
  /**
   * מזהה הפאנל, כדי שלכל כרטיס יהיה מזהה גרירה ייחודי.
   *
   * עם חמש טבלאות, מי שמשויך לשני סניפים מופיע בשתי רשימות
   * צוות. בלי המזהה הזה שני הכרטיסים נרשמים תחת אותו מזהה
   * גרירה, אחד דוחק את השני, והכרטיס שנדחק פשוט לא נגרר —
   * בלי שום שגיאה ובלי שום הסבר.
   */
  panelId?: string;
}) {
  const [search, setSearch] = useState('');
  const [showOthers, setShowOthers] = useState(false);

  const rows = useMemo(() => {
    const term = search.trim();
    // חברי צוות נוספים אינם ברשימה כברירת מחדל: הם של סניף אחר,
    // ושיבוץ שלהם הוא חריג ולא שגרה. כשפותחים אותם הם מסומנים.
    const pool = showOthers ? [...board.staff, ...otherStaff] : board.staff;

    return pool
      .filter((person) => term === '' || person.name.includes(term))
      .map((person) => {
        const constraints = board.constraints
          .filter((constraint) => constraint.employee_id === person.id)
          .sort((a, b) => a.date.localeCompare(b.date));

        const mine = (allAssignments ?? board.assignments).filter(
          (assignment) => assignment.employee_id === person.id,
        );

        return {
          person,
          shifts: mine.length,
          // השעות של השבוע מהלוח שכבר נטען, ולא עוד קריאה לשרת
          minutes: mine.reduce(
            (total, one) => total + shiftMinutes(one.effective_start, one.effective_end),
            0,
          ),
          constraints,
          // סכום המשקלים: חיובי = ביקש לעבוד, שלילי = ביקש שלא
          availability: constraints.reduce((total, one) => total + one.weight, 0),
        };
      })
      .sort(
        (a, b) =>
          // מי שחסרות לו משמרות קודם, כדי שהחלוקה תהיה הוגנת;
          // אחר כך מי שביקש לעבוד לפני מי שביקש שלא. השם אחרון,
          // כי סדר אלפביתי אינו עונה על שום שאלה.
          a.shifts - b.shifts ||
          b.availability - a.availability ||
          a.person.name.localeCompare(b.person.name, 'he'),
      );
  }, [board, search, showOthers, otherStaff, allAssignments]);

  return (
    // min-w-0 כדי שתא של גריד לא יתרחב לפי התוכן שלו ויוציא
    // סרגל גלילה אופקי לכל הסניף
    <aside className="grid min-w-0 content-start gap-2 rounded-sm border border-line bg-raised p-3">
      {/* הכותרת, החיפוש והמתג בשורה אחת: הרצועה היא כלי עבודה
          צר, ושלוש שורות כותרת מעליה מבזבזות בדיוק את הגובה
          שניסינו לחסוך */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <h2 className="m-0 text-body font-medium text-ink">
          {branchName ? `צוות ${branchName}` : 'צוות'}{' '}
          <span className="text-muted">({board.staff.length})</span>
        </h2>

        <p className="m-0 hidden text-label text-muted lg:block">
          גרור/י שם אל המשמרת. הסדר: מי שחסרות לו משמרות, ומי שביקש לעבוד.
        </p>

        <span className="grow" />

        <label className="relative grid">
          <span className="sr-only">חיפוש עובד</span>
          <Search
            size={16}
            strokeWidth={1.75}
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 my-auto ms-2 text-muted"
          />
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="חיפוש לפי שם"
            className="min-h-11 w-44 rounded-sm border border-muted bg-raised ps-8 pe-2 text-body-sm text-ink placeholder:text-muted"
          />
        </label>

        {otherStaff.length > 0 && (
          <button
            type="button"
            aria-pressed={showOthers}
            onClick={() => setShowOthers((current) => !current)}
            className={cn(
              'min-h-11 shrink-0 cursor-pointer rounded-sm border px-3 text-body-sm transition-colors',
              showOthers
                ? 'border-ink bg-olive-soft font-medium text-ink'
                : 'border-muted bg-transparent text-ink hover:bg-cream',
            )}
          >
            {showOthers ? 'להסתיר חברי צוות נוספים' : `חברי צוות נוספים (${otherStaff.length})`}
          </button>
        )}
      </div>

      {/*
        נגלל לצדדים. בצוות גדול שתי שורות במקום אחת: עשרים
        כרטיסים בשורה אחת הם שלוש מסכים של גלילה אופקית, ובשתיים
        זה מסך וחצי. הסדר נשאר "מי שחסרות לו משמרות קודם", ולכן
        מי שחשוב נמצא בתחילת הגלילה.
      */}
      <ul
        className={cn(
          'm-0 grid min-w-0 list-none grid-flow-col auto-cols-[15rem] gap-2 overflow-x-auto p-0 pb-1',
          rows.length > 6 ? 'grid-rows-2' : 'grid-rows-1',
        )}
      >
        {rows.map(({ person, shifts, minutes, constraints, availability }) => (
          <li key={person.id}>
            <StaffCard
              board={board}
              panelId={panelId}
              person={person}
              isGuest={!board.staff.some((one) => one.id === person.id)}
              homeBranch={
                branchNames
                  ? (person.branch_ids.map((id) => branchNames.get(id)).find(Boolean) ?? null)
                  : null
              }
              isHighlighted={highlightedId === person.id}
              onHighlight={onHighlight}
              onClearWeek={onClearWeek ? () => onClearWeek(person, shifts) : undefined}
              shifts={shifts}
              minutes={minutes}
              monthMinutes={monthly?.get(person.id)?.minutes ?? 0}
              monthLabel={monthLabel}
              weeklyTarget={monthly?.get(person.id)?.weekly_target ?? null}
              monthlyTarget={monthly?.get(person.id)?.monthly_target ?? null}
              monthOverride={monthly?.get(person.id)?.month_override ?? false}
              onEditBudget={() => onEditBudget(person, monthly?.get(person.id))}
              constraints={constraints}
              availability={availability}
            />
          </li>
        ))}
        {rows.length === 0 && (
          <li className="py-2 text-body-sm text-muted">אין עובד בשם הזה.</li>
        )}
      </ul>
    </aside>
  );
}

/**
 * שעות מול מכסה, כמד אחד.
 *
 * הפס עונה על "כמה עוד אפשר לתת לו" בלי לקרוא מספרים, וזו
 * השאלה שנשאלת בכל גרירה. המספר נשאר כי הוא התשובה המדויקת.
 *
 * חריגה היא אדום ומילה, ולא אדום לבד (כלל 10): "42 / 36" באדום
 * אומר למי שרואה צבעים שמשהו קרה, ו"חריגה" אומר את זה לכולם.
 *
 * בלי מכסה אין פס ואין חריגה, רק השעות. אפס מכסה אינו "בלי
 * מכסה" אלא "אסור לשבץ", ולכן null ולא 0.
 */
function HoursMeter({
  title,
  suffix,
  minutes,
  targetHours,
  isOverridden,
  onEdit,
}: {
  title: string;
  suffix?: string;
  minutes: number;
  targetHours: number | null;
  /** המכסה של החודש הזה נקבעה ידנית ואינה הקבועה של העובד. */
  isOverridden?: boolean;
  onEdit?: () => void;
}) {
  const targetMinutes = targetHours === null ? null : targetHours * 60;
  const over = targetMinutes !== null && minutes > targetMinutes;
  const close = targetMinutes !== null && !over && minutes >= targetMinutes * 0.9 && minutes > 0;
  const percent =
    targetMinutes === null || targetMinutes === 0
      ? 0
      : Math.min(100, Math.round((minutes / targetMinutes) * 100));

  const Root = onEdit ? 'button' : 'div';

  return (
    <Root
      type={onEdit ? 'button' : undefined}
      onClick={onEdit}
      aria-label={onEdit ? `קביעת מכסת שעות ל${title}` : undefined}
      className={cn(
        'grid min-w-0 gap-0.5 rounded-sm border border-transparent bg-transparent p-0 text-start',
        onEdit && 'cursor-pointer hover:border-line hover:bg-cream',
      )}
    >
      <span className="flex items-baseline gap-1 text-label text-muted">
        <span className="truncate">{title}</span>
        {suffix && <span className="truncate">· {suffix}</span>}
        {isOverridden && (
          <span className="shrink-0 text-ink" title="מכסה שנקבעה לחודש הזה">
            *
          </span>
        )}
      </span>

      <span
        className={cn(
          'tabular whitespace-nowrap text-body-sm',
          over ? 'font-semibold text-bad' : minutes > 0 ? 'text-ink' : 'text-muted',
        )}
      >
        {formatHours(minutes)}
        {targetHours !== null && <span className="text-muted"> / {targetHours}</span>}
      </span>

      {targetMinutes !== null && (
        <>
          <span
            role="progressbar"
            aria-valuenow={minutes}
            aria-valuemin={0}
            aria-valuemax={targetMinutes}
            aria-label={`${title}: ${formatHours(minutes)} מתוך ${targetHours} שעות`}
            className="block h-1 overflow-hidden rounded-pill bg-olive-soft"
          >
            <span
              className={cn(
                'block h-full rounded-pill',
                over ? 'bg-bad' : close ? 'bg-warn' : 'bg-olive',
              )}
              style={{ width: `${over ? 100 : percent}%` }}
            />
          </span>

          {over && (
            <span className="flex items-center gap-1 text-label font-medium text-bad">
              <AlertTriangle size={11} strokeWidth={2.5} aria-hidden="true" />
              חריגה של {formatHours(minutes - targetMinutes)}
            </span>
          )}
        </>
      )}
    </Root>
  );
}

function StaffCard({
  board,
  person,
  shifts,
  minutes,
  monthMinutes,
  monthLabel,
  weeklyTarget,
  monthlyTarget,
  monthOverride,
  constraints,
  availability,
  onEditBudget,
  isGuest = false,
  homeBranch = null,
  isHighlighted = false,
  onHighlight,
  onClearWeek,
  panelId,
}: {
  board: WeekBoard;
  person: WeekStaff;
  shifts: number;
  minutes: number;
  monthMinutes: number;
  monthLabel: string;
  /** מכסת שעות. null = בלי מכסה, ואז אין פס ואין חריגה. */
  weeklyTarget: number | null;
  monthlyTarget: number | null;
  /** המכסה החודשית נקבעה לחודש הזה בלבד. */
  monthOverride: boolean;
  constraints: WeekBoard['constraints'];
  availability: number;
  onEditBudget: () => void;
  /** עובד מסניף אחר, שנפתח דרך "חברי צוות נוספים". */
  isGuest?: boolean;
  homeBranch?: string | null;
  isHighlighted?: boolean;
  onHighlight?: (employeeId: string | null) => void;
  /** קיים רק כשיש מה לפנות: הכפתור אינו מופיע למי שלא משובץ. */
  onClearWeek?: () => void;
  /** הפאנל שהכרטיס הזה שייך לו. מזהה האדם נשאר שני, כך ש־DragCard קורא אותו. */
  panelId?: string;
}) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, isDragging } = useDraggable({
    id: panelId ? `staff:${person.id}:${panelId}` : `staff:${person.id}`,
    data: { kind: 'staff', employeeId: person.id },
  });

  const branchNames = board.branches
    .filter((branch) => person.branch_ids.includes(branch.id))
    .map((branch) => branch.name);

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      className={cn(
        'group/staff relative grid h-full cursor-grab touch-none content-start gap-1 rounded-sm border border-line bg-cream p-2 ps-2.5 transition-colors hover:border-muted',
        isDragging && 'opacity-40',
        // סימון: מאיר את כל השיבוצים שלו בלוח
        isHighlighted && 'border-ink ring-2 ring-ink',
        // אורח מסניף אחר. הגוון לבדו אינו ההודעה — התווית למטה היא
        isGuest && 'bg-butter/30',
        // הפס הוא קיצור דרך לעין בלבד. מה שאומר למה הוא שם הן
        // התגיות למטה, שתמיד נושאות את שם האילוץ (כלל 10).
        availability > 0 && 'border-s-2 border-s-good',
        availability < 0 && 'border-s-2 border-s-warn',
      )}
    >
      {/*
        הגרירה תופסת את כל הכרטיס, ולא רק את שורת השם.
        קודם המאזינים ישבו על הכפתור הזה, ומי שתפס את הכרטיס
        בחצי התחתון שלו — בדיוק איפה שהשעות, ששם העין נמצאת —
        פשוט לא הצליח לגרור, בלי שום סימן למה.
        המאזינים עברו לעטיפה, וכאן נשארת רק הנגישות: ידית
        המקלדת והתיאור. כפתור בתוך כפתור אינו חוקי, ולכן זה
        לא הפך לכפתור אחד גדול.
        חיישן המצביע דורש תנועה של שישה פיקסלים, ולכן לחיצה על
        כפתורי המכסה והפינוי שבתוך הכרטיס נשארת לחיצה.
      */}
      <button
        type="button"
        ref={setActivatorNodeRef}
        {...attributes}
        onClick={() => onHighlight?.(isHighlighted ? null : person.id)}
        aria-pressed={isHighlighted}
        aria-expanded={isHighlighted}
        aria-label={`${person.name}, ${shiftsLabel(shifts)} השבוע, ${formatHours(minutes)}${
          availability > 0 ? ', ביקש/ה לעבוד' : availability < 0 ? ', יש אילוץ' : ''
        }. לחיצה פותחת את הפירוט ומסמנת את המשמרות שלו בלוח`}
        className="grid justify-items-start gap-0.5 bg-transparent p-0 text-start"
      >
        <span className="flex w-full items-center gap-1">
          <span className="min-w-0 truncate text-body-sm font-medium text-ink">{person.name}</span>
          {isGuest && (
            <span className="shrink-0 rounded-pill border border-muted px-1.5 text-label text-ink">
              סניף אחר
            </span>
          )}
        </span>
        <span className="w-full truncate text-label text-muted">
          {person.job_title ?? 'ללא תפקיד'}
          {isGuest && homeBranch
            ? ` · ${homeBranch}`
            : branchNames.length > 0
              ? ` · ${branchNames.join(', ')}`
              : ''}
        </span>
      </button>

      {/*
        מצב מכווץ: שורה אחת שעונה על "כמה הוא עובד השבוע". זה מה
        שמחפשים כשסורקים עשרים שמות, והפירוט המלא הוא שאלה אחרת
        שנשאלת על אדם אחד.
      */}
      {!isHighlighted && (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-label">
          <span className="text-muted">
            {shiftsLabel(shifts)} · {formatHours(minutes)}
          </span>

          {/* חריגה אינה נסתרת גם במצב מכווץ, ותמיד עם מילה (כלל 10) */}
          {weeklyTarget !== null && minutes > weeklyTarget * 60 && (
            <span className="inline-flex items-center gap-0.5 font-medium text-bad">
              <AlertTriangle size={11} strokeWidth={2.5} aria-hidden="true" />
              חריגה
            </span>
          )}

          {constraints.length > 0 && (
            <span
              className={cn(
                'rounded-pill border px-1.5',
                constraints.some((one) => one.is_blocking)
                  ? 'border-bad/40 bg-bad/10 text-bad'
                  : availability > 0
                    ? 'border-good/40 bg-good/10 text-good'
                    : 'border-line bg-raised text-muted',
              )}
            >
              {constraints.length === 1 ? 'אילוץ אחד' : `${constraints.length} אילוצים`}
            </span>
          )}
        </div>
      )}

      {/* פינוי כל השבוע. מופיע רק כשיש מה לפנות, ואומר כמה. */}
      {onClearWeek && shifts > 0 && (
        <button
          type="button"
          onClick={onClearWeek}
          aria-label={`הסרת ${person.name} מכל ${shiftsLabel(shifts)} השבוע`}
          className="absolute top-1 end-1 hidden min-h-8 cursor-pointer items-center gap-1 rounded-sm border border-line bg-raised px-2 text-label text-muted group-hover/staff:inline-flex hover:border-bad hover:text-bad"
        >
          <X size={12} strokeWidth={2} aria-hidden="true" />
          פינוי השבוע
        </button>
      )}

      {/*
        שני מדים זה לצד זה, כל אחד עם כותרת משלו.
        קודם כל המספרים ישבו בשורה אחת מופרדת בנקודה, והעין לא
        ידעה איפה נגמר השבוע ומתחיל החודש. מה שעונה על השאלה
        "כמה עוד אפשר לתת לו" הוא הפס, ולא המספר.

        נפתח בלחיצה ולא בריחוף: הרצועה נגללת לצדדים, ותוכן
        שנפתח בריחוף היה נחתך על ידי הגלילה עצמה. הלחיצה ממילא
        מסמנת את המשמרות שלו בלוח, וזו אותה שאלה.
      */}
      <div className={cn('grid-cols-2 gap-x-2 gap-y-1', isHighlighted ? 'grid' : 'hidden')}>
        <HoursMeter
          title="השבוע"
          suffix={shiftsLabel(shifts)}
          minutes={minutes}
          targetHours={weeklyTarget}
        />
        <HoursMeter
          title={monthLabel}
          minutes={monthMinutes}
          targetHours={monthlyTarget}
          isOverridden={monthOverride}
          onEdit={onEditBudget}
        />
      </div>

      {isHighlighted && constraints.length > 0 && (
        <ul className="m-0 flex list-none flex-wrap gap-1 p-0">
          {constraints.map((constraint) => (
            <li
              key={`${constraint.date}-${constraint.shift_type_id ?? 'all'}-${constraint.name}`}
              className={cn(
                'rounded-pill border px-1.5 text-label',
                constraint.is_blocking && 'border-bad/40 bg-bad/10 text-bad',
                !constraint.is_blocking &&
                  constraint.weight > 0 &&
                  'border-good/40 bg-good/10 text-good',
                !constraint.is_blocking &&
                  constraint.weight <= 0 &&
                  'border-line bg-raised text-muted',
              )}
              title={constraint.note ?? `${formatDate(constraint.date)} · ${constraint.name}`}
            >
              {/*
                שם היום במלואו. קיצור לאות אחת חסך מקום ועלה
                בהבנה: "ח׳ מעדיף/ה" אינו דבר שאפשר לקרוא.
              */}
              {weekdayName(constraint.date)} · {constraint.name}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * מה שנראה מתחת לעכבר בזמן גרירה.
 *
 * בלי זה הכרטיס נשאר במקומו ומועם, והמשוב היחיד הוא שהתא שמתחת
 * לסמן משנה צבע. מי שגורר לא רואה *מה* הוא מחזיק, וזה בדיוק מה
 * שצריך לראות כשיש חמש טבלאות על המסך.
 */
export function DragCard({ board, activeId }: { board: WeekBoard; activeId: string | null }) {
  if (!activeId) return null;

  const [kind, id] = activeId.split(':');

  const person =
    kind === 'staff'
      ? board.staff.find((one) => one.id === id)
      : (() => {
          const assignment = board.assignments.find((one) => one.id === id);
          return assignment
            ? board.staff.find((one) => one.id === assignment.employee_id)
            : undefined;
        })();

  if (!person) return null;

  return (
    <div className="pointer-events-none flex items-center gap-1.5 rounded-sm border border-ink bg-raised px-2 py-1.5 shadow-card">
      <GripVertical size={14} strokeWidth={2} aria-hidden="true" className="shrink-0 text-muted" />
      <span className="text-body-sm font-medium text-ink">{person.name}</span>
    </div>
  );
}
